import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { fetchContact, updateContactAddress } from 'api/contacts/single';
import { retryMailPieceById } from 'api/engage';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { AddressForm } from 'components/address-form/AddressForm';
import { AddressFormValues } from 'components/address-form/types';
import { transformAddressFormToRequest } from 'components/address-form/useAccountAddressForm';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentMailing } from 'customHooks/useCurrentMailing';
import { useFormServerErrors } from 'customHooks/useFormServerErrors';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface RetryLetterFormValues extends AddressFormValues {}

const MailingRetryLetterDrawer = () => {
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { data: mailPiece } = useCurrentMailing();
    const { data: contact } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactById(accountId, mailPiece.contact_id),
        queryFn: () => fetchContact(accountId, mailPiece.contact_id),
    });
    const {
        address_1: line1,
        address_2: line2,
        city,
        zipcode: zip,
        country,
        state,
    } = contact.primary_address ?? {};
    const methods = Form.useForm<RetryLetterFormValues>({
        defaultValues: {
            line1: line1 ?? '',
            line2: line2 ?? '',
            city: city ?? '',
            zip: zip ?? '',
            stateUs: country === 'USA' ? (state ?? '') : '',
            stateIntl: country !== 'USA' ? (state ?? '') : '',
            country: country ?? 'USA',
        },
    });
    const { handleSubmit } = methods;
    const { success, error } = useToast();
    const { setAddressErrors } = useFormServerErrors(methods);
    const { goBack } = useDialogRoute();

    const { mutate: submit } = useMutation({
        mutationFn: async (values: RetryLetterFormValues) =>
            updateContactAddress(
                accountId,
                mailPiece.contact_id,
                mailPiece.primary_address_id as number,
                transformAddressFormToRequest({
                    ...values,
                    isPrimary: true,
                }),
            ),
        onSuccess: () => {
            retryMailPiece();
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, mailPiece.contact_id),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            const errors = e.response?.data.errors;
            if (errors) {
                setAddressErrors(errors);
            } else {
                error(e.response?.data?.message ?? 'Failed to update address');
            }
        },
    });

    const { mutate: retryMailPiece } = useMutation({
        mutationFn: () => retryMailPieceById(accountId, letter.id, mailPiece.id),
        onSuccess: () => {
            success('Retried letter successfully!');
            goBack();
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageTracking(accountId, letter.id),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Unable to retry mailing submission'),
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<RetryLetterFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Fix letter and retry</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <Form.Container>
                        <AddressForm />
                    </Form.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div className={'flex w-full justify-between gap-4'}>
                        <Drawer.Cancel>Cancel</Drawer.Cancel>
                        <Form.SubmitButton>Update and retry</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { MailingRetryLetterDrawer };
